// The draft on the real page, for the admin editor. Only with a ?preview= token, which the
// server checks (Controller/HomeContent.js): without one none of this runs, and a bad one
// gets the live page and a word saying so.
//
// Inside the editor's frame the page also takes the form's unsaved text by postMessage, so
// what is typed shows at once and the scroll position stays. A message counts only from
// the window that framed this page, on this origin, after the server accepted the token.
//
// A preview is never a visit. It carries noindex, its contact form sends nothing, and
// inside the frame a link does not lead away.

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchPreviewHomeContent } from "../../services/homeContentApi";
import { assetUrl } from "../../utils/api";

export const PREVIEW_CHANNEL = "ax-home-preview";

const tokenFromUrl = () => {
    try { return new URLSearchParams(window.location.search).get("preview") || ""; }
    catch { return ""; }
};
const isFramed = () => {
    try { return window.parent !== window; }
    catch { return true; }
};

/** A picture from the document: a full address as it is, otherwise one on the API's host. */
export const documentImage = (url) => {
    const value = String(url || "").trim();
    if (!value) return "";
    return /^https?:\/\//i.test(value) ? value : assetUrl(value);
};

/**
 * onContent(doc)  takes a document sent by the frame's parent.
 * onScroll(where) takes a selector for what the admin is editing.
 * @returns { token, framed, state, load } state: null with no token, then "draft" | "live" | "invalid".
 */
export function useLandingPreview({ onContent, onScroll } = {}) {
    const [token] = useState(tokenFromUrl);
    const [framed] = useState(isFramed);
    const [state, setState] = useState(null);
    const accepted = useRef(false);
    const handlers = useRef({ onContent, onScroll });
    handlers.current = { onContent, onScroll };

    useEffect(() => {
        if (!token || !framed) return undefined;
        const onMessage = (e) => {
            if (e.origin !== window.location.origin || e.source !== window.parent) return;
            if (e.data?.channel !== PREVIEW_CHANNEL || !accepted.current) return;
            if (e.data.type === "content") handlers.current.onContent?.(e.data.content);
            if (e.data.type === "scroll") handlers.current.onScroll?.(e.data.where);
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [token, framed]);

    useEffect(() => {
        if (!token) return undefined;
        const onSubmit = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        const onClick = (e) => {
            const link = e.target.closest?.("a[href]");
            if (framed && link && !String(link.getAttribute("href")).startsWith("#")) e.preventDefault();
        };
        document.addEventListener("submit", onSubmit, true);
        document.addEventListener("click", onClick, true);
        return () => {
            document.removeEventListener("submit", onSubmit, true);
            document.removeEventListener("click", onClick, true);
        };
    }, [token, framed]);

    const load = useCallback(async () => {
        const { content, preview } = await fetchPreviewHomeContent(token);
        accepted.current = preview === "draft" || preview === "live";
        setState(preview);
        return content;
    }, [token]);

    // Called once the page can take a document, after its first paint from the server's.
    const announce = useCallback(() => {
        if (framed && accepted.current) window.parent.postMessage({ channel: PREVIEW_CHANNEL, type: "ready" }, window.location.origin);
    }, [framed]);

    return { token, framed, state, load, announce };
}

// Only in a tab of its own: in the frame, the editor around it says the same.
export function PreviewNotice({ preview }) {
    if (!preview.token || preview.framed || !preview.state) return null;
    const expired = preview.state === "invalid";
    return (
        <div
            role="status"
            style={{
                position: "fixed", insetBlockEnd: 16, insetInlineStart: "50%", transform: "translateX(50%)", zIndex: 9999,
                padding: "8px 16px", borderRadius: 999, background: "var(--gr-950)", color: "var(--gr-0)",
                fontSize: "0.85rem", fontWeight: 700, whiteSpace: "nowrap", maxWidth: "92vw", overflow: "hidden", textOverflow: "ellipsis",
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.35)",
            }}
        >
            {expired ? "انتهت صلاحية رابط المعاينة: هذه هي الصفحة المنشورة." : "معاينة: هذه الصفحة لا يراها الزوار."}
        </div>
    );
}
